import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";

import CoverageReport from "./CoverageReport";
import { MESSAGE_STYLES } from "../../../utilities/constants";
import { convertMessageToHtml, getCodeTableDescriptionByCodeTableUid } from "../../../utilities/helpers";

const CFRReport = ({ inspection, inspectionDetails, codeTables, lookups, b503Operations, a503Operations }) => {
  const SYSTEM_CHOICE_CODE_TABLE = 6;
  const systemChoiceCodeTables = codeTables?.filter(
    (codeTable) => codeTable.code_table_type_id === SYSTEM_CHOICE_CODE_TABLE
  );

  const SectionContainer = styled(Box)(({ theme }) => ({
    marginBottom: 32,
    padding: '24px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  }));

  const ItemHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '1.3rem',
    color: '#2c3e50',
    marginBottom: 24,
    paddingBottom: 12,
    borderBottom: '3px solid #3498db',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  }));

  const ContentSection = styled(Box)(({ theme }) => ({
    marginBottom: 16,
    padding: '16px',
    backgroundColor: '#fafbfc',
    borderRadius: '6px',
    border: '1px solid #e8ebee',
    '& p': {
      margin: 0,
      lineHeight: 1.7,
      color: '#2c3e50',
    },
    '& div': {
      lineHeight: 1.7,
      color: '#2c3e50',
    }
  }));

  // System choice is entered by free text
  const hasSystemChoiceSummary = (systemChoice) => {
    const foundSystemChoice = lookups?.find(
      (lookup) => lookup.lookup_uid === systemChoice.code_table_uid
    );

    const foundInspectionDetail = inspectionDetails?.filter(inspectionDetail =>
      inspectionDetail.code === foundSystemChoice?.code && inspectionDetail.lookup_code_type === foundSystemChoice?.lookup_code_type)

    return foundInspectionDetail?.length > 0 ? foundInspectionDetail[0]?.detail.coverageComment : "";
  }

  const showDidNotCover = (systemChoice, systemChoiceSummary) => {
    const result = inspectionDetails?.filter(
      (inspectionDetail) =>
        inspectionDetail?.detail?.systemChoice === systemChoice.code_table_uid
    ).length > 0 ? "" : " - Did Not Cover";
    return systemChoiceSummary ? "" : result;
  };

  return systemChoiceCodeTables &&
    <Box sx={{ padding: 2 }}>
      {systemChoiceCodeTables.map((systemChoice) => {
        const systemChoiceSummary = hasSystemChoiceSummary(systemChoice); // Cache the result
        
        // Check if this system choice has any coverage or content
        const hasCoverageContent = inspectionDetails?.some(
          (inspectionDetail) =>
            inspectionDetail?.detail?.systemChoice === systemChoice.code_table_uid
        );
        // Only render if there's content or summary
        if (!hasCoverageContent && !systemChoiceSummary) {
          return null;
        }

        return (
          <SectionContainer key={systemChoice.id}>
            <ItemHeader>
              {systemChoice.description} {systemChoiceSummary ? "" : showDidNotCover(systemChoice, systemChoiceSummary)}
            </ItemHeader>

            <Box sx={{ marginBottom: 3 }}>
              <CoverageReport
                inspection={inspection}
                inspectionDetails={inspectionDetails}
                lookups={lookups}
                codeTables={codeTables}
                systemChoiceUid={systemChoice.code_table_uid}
              />
            </Box>

            {systemChoiceSummary && (
              <ContentSection>
                <div
                  dangerouslySetInnerHTML={{
                    __html: convertMessageToHtml(systemChoiceSummary),
                  }}
                  style={{
                    lineHeight: "1.7",
                    "& pre": MESSAGE_STYLES.pre,
                    "& code": MESSAGE_STYLES.code,
                    "& strong": MESSAGE_STYLES.strong,
                    "& em": MESSAGE_STYLES.em,
                  }}
                />
              </ContentSection>
            )}
          </SectionContainer>
        );
      })}
      {b503Operations?.length > 0 && (
        <SectionContainer key="other-503b">
          <ItemHeader>Other (503B)</ItemHeader>
          {[...b503Operations].sort((a, b) => (a.detail?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.detail?.sortOrder ?? Number.MAX_SAFE_INTEGER)).map((operation) => (
            <Box key={operation.inspection_503b_operation_uid || operation.code} sx={{ marginBottom: 2 }}>
              <Typography sx={{ fontWeight: 700, marginBottom: 1 }}>
                {operation.description || operation.code || "503B Operation"}
              </Typography>
              <ContentSection>
                <div>
                  {getCodeTableDescriptionByCodeTableUid(codeTables, operation?.detail?.coverageChoice)}
                </div>
                {operation?.detail?.coverageComment && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: convertMessageToHtml(operation?.detail?.coverageComment),
                    }}
                    style={{
                      marginTop: 8,
                      lineHeight: "1.7",
                      "& pre": MESSAGE_STYLES.pre,
                      "& code": MESSAGE_STYLES.code,
                      "& strong": MESSAGE_STYLES.strong,
                      "& em": MESSAGE_STYLES.em,
                    }}
                  />
                )}
              </ContentSection>
            </Box>
          ))}
        </SectionContainer>
      )}
      {a503Operations?.length > 0 && (
        <SectionContainer key="other-503a">
          <ItemHeader>Other (503A)</ItemHeader>
          {[...a503Operations].sort((a, b) => (a.detail?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.detail?.sortOrder ?? Number.MAX_SAFE_INTEGER)).map((operation) => (
            <Box key={operation.inspection_503a_operation_uid || operation.code} sx={{ marginBottom: 2 }}>
              <Typography sx={{ fontWeight: 700, marginBottom: 1 }}>
                {operation.description || operation.code || "503A Operation"}
              </Typography>
              <ContentSection>
                <div>
                  {getCodeTableDescriptionByCodeTableUid(codeTables, operation?.detail?.coverageChoice)}
                </div>
                {operation?.detail?.coverageComment && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: convertMessageToHtml(operation?.detail?.coverageComment),
                    }}
                    style={{
                      marginTop: 8,
                      lineHeight: "1.7",
                      "& pre": MESSAGE_STYLES.pre,
                      "& code": MESSAGE_STYLES.code,
                      "& strong": MESSAGE_STYLES.strong,
                      "& em": MESSAGE_STYLES.em,
                    }}
                  />
                )}
              </ContentSection>
            </Box>
          ))}
        </SectionContainer>
      )}
    </Box>
};

export default CFRReport;
